/*
 * @Copyright 2026. Institute for Future Intelligence, Inc.
 *
 * Periodic boundary box for the crystal MD engine.
 *
 * The simulation cell IS the crystal supercell — the nx·ny·nz block of
 * unit cells — tiled infinitely in every direction. With periodic
 * boundaries every molecule keeps its full neighbor shell: there is no
 * surface, so the crystal lattice becomes the genuine energy minimum
 * and ice stays ice. (A finite cluster in vacuum is mostly surface, and
 * its lowest-energy state is a reconstructed droplet, not a crystal —
 * which is what a finite ice fragment relaxes into.)
 *
 * The cell is a general triclinic parallelepiped given by three lattice
 * vectors A, B, C (ice Ih is hexagonal, so an orthorhombic-only box
 * would not serve). The minimum-image displacement between two points
 * is found in fractional coordinates: s = H⁻¹·d, round each component
 * to the nearest integer and subtract, then d = H·s.
 */
import { Matrix3, Vector3 } from 'three';

export class PeriodicBox {
  /** Column matrix [A | B | C] of the supercell lattice vectors. */
  readonly h = new Matrix3();
  /** Inverse of h — maps a Cartesian vector to fractional coordinates. */
  readonly hInv = new Matrix3();
  /**
   * Smallest perpendicular width of the cell (Å). The minimum-image
   * convention is only valid for interaction cutoffs below half this
   * value, so it caps the LJ and Coulomb cutoffs.
   */
  minWidth = 0;
  /** Cell volume |a · (b × c)| (Å³). */
  volume = 0;

  /**
   * True when the cell is axis-aligned orthorhombic (a‖x, b‖y, c‖z).
   * minimumImage() then takes a cheap per-axis wrap instead of two 3×3
   * matrix products — and most supercells (cubic ice, rock-salt,
   * metals) ARE orthorhombic, so this is the common path.
   */
  private orthorhombic = false;
  private lx = 0;
  private ly = 0;
  private lz = 0;

  setLattice(a: Vector3, b: Vector3, c: Vector3): void {
    // three.js Matrix3.set() takes its arguments row-major; we want the
    // lattice vectors as COLUMNS, hence (a.x, b.x, c.x, a.y, b.y, ...).
    this.h.set(a.x, b.x, c.x, a.y, b.y, c.y, a.z, b.z, c.z);
    this.hInv.copy(this.h).invert();
    // Perpendicular width along each axis = cell volume / opposite-face
    // area. The smallest of the three is the minimum-image limit.
    const bc = new Vector3().crossVectors(b, c);
    const ca = new Vector3().crossVectors(c, a);
    const ab = new Vector3().crossVectors(a, b);
    const volume = Math.abs(a.dot(bc));
    this.volume = volume;
    this.minWidth = Math.min(volume / bc.length(), volume / ca.length(), volume / ab.length());
    // Detect the axis-aligned orthorhombic case (all off-axis components
    // zero) — then minimum image is a per-axis wrap.
    const offAxis = Math.abs(a.y) + Math.abs(a.z) + Math.abs(b.x) + Math.abs(b.z) + Math.abs(c.x) + Math.abs(c.y);
    this.orthorhombic = offAxis < 1e-9;
    this.lx = a.x;
    this.ly = b.y;
    this.lz = c.z;
  }

  /**
   * Rewrite a displacement vector to its minimum image, in place.
   * Returns the same vector so callers can chain.
   */
  minimumImage(d: Vector3): Vector3 {
    if (this.orthorhombic) {
      d.x -= this.lx * Math.round(d.x / this.lx);
      d.y -= this.ly * Math.round(d.y / this.ly);
      d.z -= this.lz * Math.round(d.z / this.lz);
      return d;
    }
    d.applyMatrix3(this.hInv); // Cartesian → fractional
    d.x -= Math.round(d.x);
    d.y -= Math.round(d.y);
    d.z -= Math.round(d.z);
    d.applyMatrix3(this.h); // fractional → Cartesian
    return d;
  }

  /** The three supercell lattice vectors [A, B, C] (columns of h). */
  latticeVectors(): [Vector3, Vector3, Vector3] {
    const e = this.h.elements; // column-major: e[0..2] = A, e[3..5] = B, e[6..8] = C
    return [new Vector3(e[0], e[1], e[2]), new Vector3(e[3], e[4], e[5]), new Vector3(e[6], e[7], e[8])];
  }

  /**
   * The cell's extent along each lattice vector (Å) — the container the
   * engine reports while periodic. For an orthorhombic cell these are the
   * box side lengths; for a skewed cell they are |A|, |B|, |C|.
   */
  extents(): { lx: number; ly: number; lz: number } {
    const [a, b, c] = this.latticeVectors();
    return { lx: a.length(), ly: b.length(), lz: c.length() };
  }
}
